import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Sparkles, Compass, Search, Home as HomeIcon } from 'lucide-react';
import SEO from '../components/SEO';
import { BRAND } from '../config/brand';

const NotFound = () => {
  const navigate = useNavigate();

  return (
    <>
      <SEO
        title="404 — Страница не найдена"
        description={`Похоже, вы забрели в неизведанные земли ${BRAND.name}. Давайте вернемся к каталогу!`}
        structuredData={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "404 — Страница не найдена",
          "description": `${BRAND.name} — ${BRAND.claimRu}`,
          "isPartOf": {
            "@type": "WebSite",
            "name": BRAND.name,
            "url": BRAND.baseUrl
          }
        }}
      />
      <style>{`
        @keyframes float1 {
          0% { transform: translateY(0) translateX(0) rotate(0deg); opacity: 0.8; }
          50% { transform: translateY(-18px) translateX(6px) rotate(6deg); opacity: 1; }
          100% { transform: translateY(0) translateX(0) rotate(0deg); opacity: 0.8; }
        }
        @keyframes float2 {
          0% { transform: translateY(0) translateX(0) rotate(0deg); opacity: 0.7; }
          50% { transform: translateY(-12px) translateX(-10px) rotate(-6deg); opacity: 1; }
          100% { transform: translateY(0) translateX(0) rotate(0deg); opacity: 0.7; }
        }
        @keyframes glowPulse {
          0%, 100% { box-shadow: 0 0 0px rgba(99,102,241,0.0), 0 0 0px rgba(59,130,246,0.0); }
          50% { box-shadow: 0 10px 40px rgba(99,102,241,0.25), 0 8px 24px rgba(59,130,246,0.25); }
        }
        .animate-float1 { animation: float1 6s ease-in-out infinite; }
        .animate-float2 { animation: float2 7s ease-in-out infinite; }
        .animate-glow { animation: glowPulse 3.5s ease-in-out infinite; }
        .gradient-text {
          background: linear-gradient(90deg, #22d3ee, #6366f1, #ef4444);
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
        }
      `}</style>

      <div className="relative min-h-[calc(100vh-4rem)] bg-secondary-50 overflow-hidden">
        {/* Decorative background like on Hero */}
        <div className="absolute inset-0 hero-gradient"></div>
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="glass-bg"></div>
          <div className="glass-bg"></div>
          <div className="glass-bg"></div>
          <div className="glass-bg"></div>

          {/* Floating elements */}
          <div className="absolute top-16 left-10 w-24 h-24 rounded-2xl bg-white/10 border border-white/20 backdrop-blur-md animate-float1"></div>
          <div className="absolute bottom-20 right-14 w-28 h-28 rounded-full bg-white/10 border border-white/20 backdrop-blur-md animate-float2"></div>
          <div className="absolute top-1/3 right-1/4 w-16 h-16 rounded-xl bg-white/10 border border-white/20 backdrop-blur-md animate-float1" style={{ animationDelay: '0.8s' }}></div>
          <div className="absolute bottom-1/4 left-1/5 w-14 h-14 rounded-full bg-white/10 border border-white/20 backdrop-blur-md animate-float2" style={{ animationDelay: '0.5s' }}></div>
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-4 py-14 sm:py-20 text-white" id="notfound">
          <div className="text-center">
            <div className="glass-card p-6 sm:p-10 max-w-3xl mx-auto mb-10">
              <div className="flex flex-col items-center">
                <div className="text-[70px] sm:text-[96px] leading-none font-extrabold gradient-text tracking-tight">
                  404
                </div>
                <h1 className="mt-3 sm:mt-4 text-2xl sm:text-3xl font-bold">
                  Страница не найдена
                </h1>
                <p className="mt-3 sm:mt-4 text-white/90 max-w-2xl">
                  Похоже, вы забрели в неизведанные земли. Давайте вернёмся к каталогу на {BRAND.name}.
                </p>

                <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3 w-full">
                  <button
                    onClick={() => navigate('/')}
                    className="glass-card border-2 border-white px-4 py-3 sm:py-4 flex items-center justify-center space-x-2 hover:scale-[1.02] transition-transform duration-200"
                    title="На главную"
                  >
                    <HomeIcon className="w-5 h-5" />
                    <span>На главную</span>
                  </button>
                  <Link
                    to="/search"
                    className="glass-card border-2 border-white px-4 py-3 sm:py-4 flex items-center justify-center space-x-2 hover:scale-[1.02] transition-transform duration-200"
                    title="Поиск идей"
                  >
                    <Search className="w-5 h-5" />
                    <span>Поиск идей</span>
                  </Link>
                  <Link
                    to="/"
                    className="glass-card border-2 border-white px-4 py-3 sm:py-4 flex items-center justify-center space-x-2 hover:scale-[1.02] transition-transform duration-200"
                    title="Открыть рекомендации"
                  >
                    <Compass className="w-5 h-5" />
                    <span>Открыть рекомендации</span>
                  </Link>
                </div>
              </div>
            </div>

            {/* Helpful panels matching the concept */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-5xl mx-auto">
              <div className="bg-white/85 text-secondary-900 rounded-xl shadow-soft p-5 border border-white/40">
                <div className="flex items-center space-x-2 mb-2">
                  <Compass className="w-5 h-5 text-primary-500" />
                  <h3 className="font-semibold">Исследуйте тренды</h3>
                </div>
                <p className="text-secondary-600 text-sm">
                  Откройте популярные доски и идеи сообщества.
                </p>
              </div>
              <div className="bg-white/85 text-secondary-900 rounded-xl shadow-soft p-5 border border-white/40">
                <div className="flex items-center space-x-2 mb-2">
                  <Search className="w-5 h-5 text-primary-500" />
                  <h3 className="font-semibold">Найдите вдохновение</h3>
                </div>
                <p className="text-secondary-600 text-sm">
                  Ищите по ключевым словам и категориям.
                </p>
              </div>
              <div className="bg-white/85 text-secondary-900 rounded-xl shadow-soft p-5 border border-white/40">
                <div className="flex items-center space-x-2 mb-2">
                  <Sparkles className="w-5 h-5 text-primary-500" />
                  <h3 className="font-semibold">Создайте свой момент</h3>
                </div>
                <p className="text-secondary-600 text-sm">
                  Поделитесь своим креативом — создайте новый пин.
                </p>
              </div>
            </div>

            <div className="mt-10">
              <button
                onClick={() => navigate(-1)}
                className="glass-card border-2 border-white px-6 py-3 hover:scale-105 transition-transform duration-200"
                title="Вернуться назад"
              >
                Вернуться назад
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default NotFound;
